import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { safeCustomElement } from '@/utilities/custom-element'
import { ScrollableTable } from './ScrollableTable'
import type { DataTableArgs, TableColumn } from './types'

type ProcessedTableColumn = {
  label: string
  width: string | undefined
  align: TableColumn['align']
  displayOptions: {
    visibleInScrollableView: boolean
    widthInScrollableView: string | undefined
  }
}

function applyColumnDefaults(column: TableColumn): ProcessedTableColumn {
  const defaultDisplayOptions = {
    visibleInScrollableView: true,
    widthInScrollableView: column.width,
  }

  return {
    label: column.label,
    width: column.width,
    align: column.align || 'left',
    displayOptions: {
      ...defaultDisplayOptions,
      ...column.displayOptions,
    },
  }
}

@safeCustomElement('mdc-data-table')
export class MdcDataTable extends LitElement {
  @property({ type: Array }) columns: TableColumn[] = []
  @property({ type: Array }) rows: DataTableArgs['rows'] = []
  @property({ type: Number, attribute: 'sticky-start-columns' }) stickyStartColumns = 0
  @property({ type: Boolean, attribute: 'scroll-x' }) scrollX = true
  @property({ type: Boolean, attribute: 'scroll-y' }) scrollY = true

  static styles = css`
    :host {
      display: block;
    }
    .wrapper {
      overflow-x: var(--_overflow-x, auto);
      overflow-y: var(--_overflow-y, auto);
    }
  `

  render() {
    const overflowStyles = `--_overflow-x: ${this.scrollX ? 'auto' : 'hidden'}; --_overflow-y: ${this.scrollY ? 'auto' : 'hidden'}`

    // Markup mode: render slotted HTML table content
    if (this.childElementCount > 0) {
      return html`<div class="wrapper" style=${overflowStyles}><slot></slot></div>`
    }

    const processedColumns = this.columns.map(applyColumnDefaults)

    const scrollableTable = new ScrollableTable(
      processedColumns
        .filter((column) => column.displayOptions.visibleInScrollableView !== false)
        .map((column) => ({
          label: column.label,
          width: column.displayOptions.widthInScrollableView || column.width,
          align: column.align,
        })),
      this.rows.map((row) => ({
        cells: row.cells.filter(
          (_, index) => processedColumns[index].displayOptions.visibleInScrollableView !== false
        ),
        href: row.href,
      })),
      this.stickyStartColumns
    )

    const htmlContent = scrollableTable.render()

    return html`<div class="wrapper" style=${overflowStyles}>${unsafeHTML(htmlContent)}</div>`
  }
}
